
import img0 from './AddJdbcClasspath.png';
import img1 from './GenerateJdbcUserToken.png';
  
const content = `# OnData JDBC Database Driver Overview

The OnData database drivers can be used to enforce the data access policies for the confidential and regulated data. The data access roles, just like the application user roles, can 
be assigned to a user when the user signs into applications through an identity and access management (IAM) system. Such data access roles are embedded in the user's token. When such 
user tokens are passed to an OnData database driver, the driver will validate such tokens, identify the data access roles explicitly granted to a user, and enforce the data access 
permissions so that the user can only retrieve or store data that the user has access to.  

The OnData JDBC driver (**com.ondata.jdbc**) is a Java JDBC API implementation that can access any kind of tabular data, especially data stored in a relational database. It implements 
the interfaces in the **java.sql** package and enables the dynamic encryption and decryption of the confidential and regulated data. Minimal code changes are required to pass the user 
tokens to the OnData database driver.

OnData JDBC features:

- Data Encryption: For table columns with encryption rules configured at the OnData Portal, data will be encrypted before being stored to database. The strong 
encryption significantly enhances the protection of the confidential and regulated data against both external and internal threats.
- Data Decryption: If a user does have access to the encrypted data, such data will be seamlessly decrypted for the user. Otherwise, the data will stay as encrypted or masked 
per the masking rules configured at the OnData Portal.

## 1. How to Use OnData JDBC

### 1.1 Add Classpath to com.ondata.jdbc

Download the **ondata-jdbc-11.x.x-jar-with-dependencies.jar** package from OnData and in your project add a Classpath to that JAR file, as illustrated below.

![Add Classpath](${ img0 })

**Note**: If the JDBC classpath of the corresponding database has been added to your project, please remove it.

### 1.2 Add OnData JDBC Dependencies

Inside the OnData driver package, you will find the list of OnData JDBC dependency files as illustrated below. Such files are required for running your applications with 
the ondata-jdbc-11.x.x-jar-with-dependencies.jar. There is no need to add reference to them in your project, but you need to have them in the same directory as your compiled applications.  
  
**Windows**:
- EncryptSQL.dll: OnData driver library for Windows
- libcrypto-1_1-x64.dll: From the OpenSSL library
- libpq.dll: From the OpenSSL library
- libssl-1_1-x64.dll: From the OpenSSL library
- OnDataJdbc.properties: OnData driver configuration file
- Configurator.exe: OnData driver configuration utility
- gdroot-g2.crt: The root certificate from the GoDaddy Class 2 Certification Authority

**Linux**:
- EncryptSQL.so: OnData driver library for Linux.
- OnDataJdbc.properties: OnData driver configuration file
- Configurator: OnData driver configuration utility
- gdroot-g2.crt: The root certificate from the GoDaddy Class 2 Certification Authority

### 1.3 Register OnData JDBC Driver as OnData Client

Before the OnData JDBC driver can be used, it must be registered and activated as an OnData client. For each OnData JDBC driver, you can either run the OnData Configurator tool 
included in the OnData JDBC driver release package to register it as a driver client, or create a driver client directly at the OnData Portal and populate the corresponding 
ClientId and ClientSecret information from the OnData Portal to the OnDataJdbc.properties file. For details on how to register a driver client with the OnData Platform, please refer to 
the [**OnData Client Registration**](reference_doc#how-to_guides/drivers/client_registration).  

### 1.4 Configure OnDataJdbc.properties File

The OnData JDBC driver will look for the OnDataJdbc.properties file in the same directory the ondata-jdbc-11.x.x-jar-with-dependencies.jar file is located. The OnDataJdbc.properties file 
contains the following configuration entries:

- The type of the database (MSSQL, ORACLE, POSTGRESQL, MYSQL, or MARIADB)
- The full path to the jar package for the default database driver
- The ApplicationId, which should always be ondata-data-driver
- The optional path where the log files should be stored
- The optional log level (NONE, FATAL, ERROR, WARNING, INFO, DEBUG, or VERBOSE)
- The optional flag on whether the audit log is enabled
  
**OnDataJdbc.properties**:
\`\`\`properties
###### Configuration for OnDataJdbc ################

# The database type (MSSQL, ORACLE, POSTGRESQL, MYSQL, or MARIADB)
DatabaseType = ORACLE

# The full path to the jar package for the default database driver
DriverFullPath = file:/D:/driverjars/ojdbc11-21.5.0.0.jar

# The ApplicationId, which should always be ondata-data-driver
ApplicationId = ondata-data-driver

# The optional path where the log files should be stored
LogPath = C:\\\\ondata\\\\driverlogs\\\\

# The optional log level, default is ERROR
# NONE=0, FATAL=1, ERROR=2, WARNING=3, INFO=4, DEBUG=5, or VERBOSE=6
LoggingLevel = 2

# The optional flag on whether the audit log is enabled, default is false
EnableAuditLog = false

\`\`\`

### 1.5 Use OnData JDBC Driver to Create Connection 

To create a connection using the OnData JDBC driver, first use "Class.forName" to create an instance of com.ondata.jdbc.OnDataJDBCDriver. Then use the DriverManager.getConnection() method, 
passing in a database connection string, to get a database connection through the OnData JDBC driver.
  
Below is the sample code for connecting to a database using the OnData JDBC driver.  

\`\`\`java
/**
  * creates connection
  * 
  * @return
  */
private Connection connect() {
  try {
    Class.forName("com.ondata.jdbc.OnDataJDBCDriver").newInstance();
  } catch (Exception e) {
    e.printStackTrace();
  } 

  try {
    return DriverManager.getConnection("jdbc:oracle:thin:demo/demopwd@localhost:1521/XE");
  } catch (SQLException e) {
    e.printStackTrace();
    return null;
  }
}
\`\`\`

### 1.6 Set User Token

In order to manage the data access for each user signing into your application, the application must be able to generate or get user tokens upon user sign in. A user token is a 
JWT (JSON Web Token) which contains a list of data access roles. The data access roles are configured at the OnData Portal and can represent the read and write permissions 
for different data classifications. In your application, you can set a user token to a **Statement** or **PreparedStatement** and then this statement will use that user token to 
access data through the OnData driver. It is recommended to set a user token per statement created from a connection.

![Set user token](${ img1 })

A user token can be either generated by your application itself or obtained from a commercial off-the-shelf Identity and Access Management (IAM) system, such as Azure AD and Okta.

#### 1.6.1 Generate User Token in Application
If you choose to generate the user tokens through your application, please follow the steps below:

- Generate s RSA key pair, upload the public key to OnData Portal (under Settings->Encryption->Keys) as the token validation key. OnData driver will use it to validate the user token.
- Figure out the data access roles of the login user. You can get the user info from HttpContext or Session, then determine the data access roles assigned to that login user. If the connection 
is used to process some background tasks that are not associated with any specific user, then determine data access roles needed for that background process.
- Generate a JWT with the RSA private key, and add an attribute called dataAccessRoles, which is an array of data access roles, as a claim to the payload. It is recommended that the 
username is also added to the JWT token in case you want to log data access history.
- The com.ondata.jdbc.Statement provides a method to pass the user token to the Statement (com.ondata.jdbc.PreparedStatement inherits from com.ondata.jdbc.Statement).  

Below is the sample code for generating a user token for a given username and associated data access roles.  
  
\`\`\`java
import javax.crypto.spec.SecretKeySpec;
import javax.xml.bind.DatatypeConverter;
import java.security.Key;
import java.util.Date;
import java.util.HashMap;
import java.util.List;
import java.util.Map;
import java.util.UUID;
import io.jsonwebtoken.*;
import io.jsonwebtoken.Jwts;
import io.jsonwebtoken.Claims;

public class JWTDemo {

	// The private key. This should be in a property file and not hard coded or under source control
  // It is included here for illustration purpose
  private static String SECRET_KEY = "oeRaYY7Wo24sDqKSX3IM9ASGmdGPmk......";

  // Sample method to construct a JWT
  public static String generateUserToken(List<String> dataAccessRoles, String issuer, String username) {
    
    //The JWT signature algorithm to sign the token
    SignatureAlgorithm signatureAlgorithm = SignatureAlgorithm.RS256;

    long nowMillis = System.currentTimeMillis();
    Date now = new Date(nowMillis);

    byte[] apiKeySecretBytes = DatatypeConverter.parseBase64Binary(SECRET_KEY);
    Key signingKey = new SecretKeySpec(apiKeySecretBytes, signatureAlgorithm.getJcaName());

    // Add the data access roles to the claims
    Map<String, Object> claimsMap= new HashMap<String, Object>();
    claimsMap.put("dataAccessRoles", dataAccessRoles);
    
    JwtBuilder builder = Jwts.builder().setId(UUID.randomUUID().toString())
      .setIssuedAt(now)
      .setSubject(username)
      .setIssuer(issuer)
      .setClaims(claimsMap)
      .signWith(signatureAlgorithm, signingKey);

    // Set the token expiration time to 30 minutes
    long expMillis = nowMillis + 30 * 60 * 1000;
    Date exp = new Date(expMillis);
    builder.setExpiration(exp);

    // Build the JWT and serializes it to a compact, URL-safe string
    return builder.compact();
  }
}
\`\`\`

#### 1.6.2 Get User Token from IAM System
If your application is integrated with an Identity and Access Management (IAM) system for user authentication and authorization management, please follow the steps below:  
- Add a custom claim named dataAccessRoles into access_token.
- Upload the public key(s) of the IAM System to the OnData Portal (under Settings->Encryption->Keys) as token validation key(s).
- Get access_token from the HttpContext or Session object.
- The com.ondata.jdbc.Statement provides a method to pass the user tokens to the Statement instance.  

Below is the sample code to get the access_token from a session object and pass it to a  PreparedStatement object.  
  
\`\`\`java
String userToken = (String)session.getAttribute("access_token");
Connection conn = MyUtil.createDBConnection(connStr);
PreparedStatement ps = null;
ResultSet rs = null;
try {
    String sql = "​select * from dbo.test_float_number where numeric_numer = ?";
    ps = conn.prepareStatement(sql);
    ((com.ondata.jdbc.OnDataPreparedStatement)ps).setUserToken(userToken);
    ps.setBigDecimal(1, new BigDecimal("2.0000"));
    rs = ps.executeQuery();
    while(rs.next()) {
      // Read rows from the ResultSet 
    }
  }catch (Exception e) {
    e.printStackTrace();
  } finally {
    MyUtil.closeDBResource(rs, ps, conn);
}

\`\`\`

## 2. Possible Exceptions

If something goes wrong, one of the following error codes could be returned. 

\`\`\`java
// Error codes:
NULL_ENCRYPT_CONTROLLER(-1),
FAILED_CONNECT_ONDATA(-2),
FAILED_PARSE_STATEMENT(-3),
FAILED_EXECUTION_PERMISSION(-4),
STATEMENT_NOT_FOUND(-5),
PARAMETER_NOT_FOUND(-6),
COLUMN_NOT_FOUND(-7),
FAILED_PARAMETER_ENCRYPTION(-8),
FAILED_VALUE_DECRYPTION(-9),
FAILED_INSUFFICIENT_BUFFER(-10),
FAILED_DRIVER_NOT_CONFIGURED(-11);

\`\`\`
`
  
export default content;
